import { RESOLVER_CONFLITO_LABEL } from "@/shared/components/gantt-carga/gantt-carga.constants";
import type { MenuItem } from "primereact/menuitem";

import { GANTT_LAYOUT_PRESETS } from "@/ds";
import type { MarcasSimulacao } from "@/modules/recursos/simulacao";
import { Gantt as GanttBase, lanesMinHeightRem } from "@/shared/components/gantt";
import type { GanttColumn, GanttRow } from "@/shared/components/gantt";

import type { GanttRowModel } from "../gantt-carga/gantt-rows";
import { ConflictBadge } from "../conflict-badge";
import { ConflictBand } from "../conflict-band";
import { AllocationBar } from "../gantt-allocation-bar";
import { SIMULACAO_MARCA_CLASSES, SimulacaoMarcaTag } from "../simulacao-marca";
import {
  LANE_PADDING_IGNORED_REM,
  LANE_PADDING_REM,
  LAYOUT_IGNORED,
  LAYOUT_PRESET,
} from "./gantt-profissionais.constants";
import type { GanttProfissionaisViewModel } from "./types-gantt-profissionais";
import styles from "../gantt-carga/gantt.module.scss";

function conflictBandClass(band: GanttRowModel["bands"][number]): string | undefined {
  if (band.highlight) return styles.conflictBandHighlight;
  return band.ignored ? styles.conflictBandIgnored : styles.conflictBand;
}

function conflictBandTestId(band: GanttRowModel["bands"][number]): string {
  if (band.highlight) return "gantt-highlight-band";
  return band.ignored ? "gantt-conflict-band-ignored" : "gantt-conflict-band";
}

function toRow(
  row: GanttRowModel,
  rowMinHeightRem: number,
  marcaDaLinha: GanttProfissionaisViewModel["marcaDaLinha"],
  rotuloConflito: GanttProfissionaisViewModel["rotuloConflito"],
  marcas: MarcasSimulacao,
  onResolverConflito?: (row: GanttRowModel) => void,
): GanttRow {
  const marcaDaLinhaAtual = marcaDaLinha(row);
  const hasIgnored = row.bands.some((band) => band.ignored);
  const layout = hasIgnored ? { ...LAYOUT_IGNORED } : LAYOUT_PRESET;
  const comConflito = Boolean(onResolverConflito) && row.conflicts > 0;
  const actions: MenuItem[] | undefined =
    onResolverConflito && comConflito
      ? [
          {
            label: RESOLVER_CONFLITO_LABEL,
            icon: "pi pi-thumbs-up",
            command: () => onResolverConflito(row),
          },
        ]
      : undefined;

  return {
    key: row.key,
    testId: `gantt-row-${row.kind}`,
    className: marcaDaLinhaAtual ? SIMULACAO_MARCA_CLASSES.linha : undefined,
    minHeight: `${lanesMinHeightRem({
      lanes: row.lanes,
      laneHeightRem: hasIgnored
        ? LAYOUT_IGNORED.laneHeightRem
        : GANTT_LAYOUT_PRESETS[LAYOUT_PRESET].laneHeightRem,
      paddingRem: hasIgnored ? LANE_PADDING_IGNORED_REM : LANE_PADDING_REM,
      minRem: rowMinHeightRem,
    })}rem`,
    actionLabel: comConflito ? `Ações de ${row.title}` : `Detalhes de ${row.title}`,
    actions,
    cells: [
      <div key="copy" className={styles.leftCopy}>
        <span className={styles.rowTitle}>{row.title}</span>
        {row.subtitle ? <span className={styles.rowSubtitle}>{row.subtitle}</span> : null}
        {row.meta || row.conflicts > 0 ? (
          <span className={styles.rowMeta}>
            {row.meta}
            {row.conflicts > 0 ? (
              <ConflictBadge
                label={rotuloConflito(row.conflicts)}
                groups={row.conflictGroups}
                tooltip={row.conflictTooltip}
              />
            ) : null}
          </span>
        ) : null}
        {marcaDaLinhaAtual ? <SimulacaoMarcaTag marca={marcaDaLinhaAtual} /> : null}
      </div>,
    ],
    content: (
      <>
        {row.bands.map((band) =>
          band.groups && band.groups.length > 0 ? (
            <ConflictBand
              key={band.key}
              groups={band.groups}
              tratado={Boolean(band.ignored)}
              left={band.left}
              width={band.width}
              className={conflictBandClass(band)}
            />
          ) : (
            <span
              key={band.key}
              className={conflictBandClass(band)}
              style={{ left: `${band.left}%`, width: `${band.width}%` }}
              data-gantt-tip
              data-pr-tooltip={band.tooltip}
              data-testid={conflictBandTestId(band)}
            />
          ),
        )}
        {row.items.map((item) => (
          <AllocationBar key={item.key} item={item} layout={layout} marca={marcas.get(item.key)} />
        ))}
      </>
    ),
  };
}

/** Gantt da visão por Profissionais: uma linha por profissional, com as barras das obras. */
export function GanttProfissionaisView({
  rows,
  today,
  headerLabel,
  onResolverConflito,
  marcas,
  marcaDaLinha,
  rotuloConflito,
  scale,
  rowMinHeightRem,
  unitMinWidth,
  comAcoes,
}: GanttProfissionaisViewModel) {
  const columns: GanttColumn[] = [
    {
      key: "profissional",
      header: headerLabel,
      width: "var(--gantt-left)",
      cellClassName: styles.leftCell,
    },
  ];

  return (
    <GanttBase.Root
      className={styles.root}
      corpoRola
      scale={scale}
      today={today}
      columns={columns}
      rows={rows.map((row) =>
        toRow(row, rowMinHeightRem, marcaDaLinha, rotuloConflito, marcas, onResolverConflito),
      )}
      withActions={comAcoes}
      grid="none"
      unitMinWidth={unitMinWidth}
    />
  );
}
